/* eslint-disable lit/no-native-attributes */
import { fontAwesome } from '@treasury/omega/css/icons/font-awesome.js';
import { TmBaseComponent } from '@treasury/presentation';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export const tagName = 'jhd-tooltip';
@customElement(tagName)
export class JhdTooltip extends TmBaseComponent {
    @property({ type: Boolean, reflect: true })
    isClickable = false;

    @property({ type: String })
    direction = 'top';

    @property({ type: String })
    icon = '';

    @property({ type: Boolean, reflect: true })
    light = false;

    @property({ type: String })
    message = '';

    @property({ type: Boolean, reflect: true })
    show = false;

    @property({ type: String })
    class = '';

    @state()
    private _boundListenForDocumentClicks: EventListenerOrEventListenerObject =
        this._listenForDocumentClicks.bind(this) as any;

    @state()
    private _boundListenForKeydown: EventListenerOrEventListenerObject =
        this._listenForKeydown.bind(this) as any;

    @state()
    private tooltipBody: HTMLElement | undefined | null = undefined;

    @state()
    relativePosition: DOMRect = {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        toJSON: () => '',
    };

    @state()
    boundDismiss: () => void = this._hideTooltip.bind(this);

    @state()
    width = 0;

    @state()
    height = 0;

    static get meta() {
        return {
            docUrl: 'https://banno.github.io/treasury-management/?path=/docs/components-toggle--toggle',
        };
    }

    connectedCallback(): void {
        // eslint-disable-next-line wc/guard-super-call
        super.connectedCallback();
        this._boundListenForDocumentClicks = this._listenForDocumentClicks.bind(this) as any;
        this._boundListenForKeydown = this._listenForKeydown.bind(this) as any;
    }

    firstUpdated() {
        this.tooltipBody = this.shadowRoot?.querySelector('.jhd-tooltip');
        this._setDimensions();
        this.relativePosition = this.getBoundingClientRect();
        this._setTooltipClass();
        if (this.isClickable) {
            this.addEventListener('click', this._showTooltip);
        }
    }

    _toggleShow() {
        if (this.isClickable) {
            this.show = !this.show;
        }

        this._addOrRemoveListeners(this.show);
    }

    _addOrRemoveListeners(show: boolean) {
        if (show) {
            this.focus();
            document.addEventListener('keydown', this._boundListenForKeydown);
            document.addEventListener('click', this._boundListenForDocumentClicks);
        } else {
            document.removeEventListener('keydown', this._boundListenForKeydown);
            document.removeEventListener('click', this._boundListenForDocumentClicks);
        }
    }

    _listenForDocumentClicks(e: MouseEvent) {
        const shouldClose = !(e.composedPath() || []).includes(this);

        if (shouldClose) {
            e.preventDefault();
            this._hideTooltip();
        }
    }

    _listenForKeydown(e: KeyboardEvent) {
        if (e.keyCode === 27) {
            this._hideTooltip();
        }
    }

    _showTooltip() {
        if (this.isClickable) {
            this.show = true;
            this._addOrRemoveListeners(this.show);
            this.removeEventListener('click', this._showTooltip);
        }
    }

    _hideTooltip() {
        if (this.isClickable) {
            this.show = false;
            window.requestAnimationFrame(() => {
                this.addEventListener('click', this._showTooltip);
            });
        }
    }

    _setDimensions() {
        this.height =
            parseInt(
                window.getComputedStyle(this.tooltipBody || document.createElement('div')).height,
                10
            ) +
            parseInt(
                window.getComputedStyle(this.tooltipBody || document.createElement('div'))
                    .paddingTop,
                10
            ) +
            parseInt(
                window.getComputedStyle(this.tooltipBody || document.createElement('div'))
                    .paddingBottom,
                10
            );
        this.width =
            parseInt(
                window.getComputedStyle(this.tooltipBody || document.createElement('div')).width,
                10
            ) +
            parseInt(
                window.getComputedStyle(this.tooltipBody || document.createElement('div'))
                    .paddingRight,
                10
            ) +
            parseInt(
                window.getComputedStyle(this.tooltipBody || document.createElement('div'))
                    .paddingLeft,
                10
            );
    }

    _setTooltipClass() {
        const direction = this._setTooltipDirection();
        const brightness = this.light ? 'tooltip-light' : '';
        this.class = `jhd-tooltip ${brightness} ${direction}`;
    }

    _setTooltipDirection() {
        if (this.direction === undefined) return this._calcTooltipDirection();
        if (this.direction.trim().length === 0) return this._calcTooltipDirection();
        return `tooltip-${this.direction}`;
    }

    _calcTooltipDirection() {
        if (this.height > this.relativePosition.y) return 'tooltip-bottom';
        if (this.width / 2 > this.relativePosition.x) return 'tooltip-right';
        if (this.width > window.innerWidth - this.relativePosition.x) return 'tooltip-left';
        return 'tooltip-top';
    }

    renderIcon() {
        if (!this.icon) return nothing;
        return html`<i
            class="jhd-tooltip-icon fa fa-${this.icon}"
            @click=${this._toggleShow}
            @keypress=${(e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                    this._toggleShow();
                }
            }}
        ></i>`;
    }

    renderTooltipContent() {
        if (this.message) return html`${this.message}`;
        if (!this.isClickable) {
            return html`<div>
                <slot name="content"></slot>
            </div> `;
        }
        return html`<div>
            <button
                id="tooltip-close-button"
                class="jhd-tooltip-close-button"
                @click=${this.boundDismiss}
            >
                <i class="fa fa-times jhd-tooltip-close" role="button" aria-label="Close"></i>
            </button>
            <slot name="content"></slot>
        </div>`;
    }

    renderTooltipTrigger() {
        if (this.isClickable) {
            return html`<button id="tooltip-trigger" @click=${this._toggleShow}>
                <slot></slot>
            </button>`;
        }
        return html`<div id="tooltip-trigger">
            <slot></slot>
        </div>`;
    }

    render() {
        return html`<span>
            ${this.renderIcon()} ${this.renderTooltipTrigger()}
            <div class=${this.class}>${this.renderTooltipContent()}</div>
        </span>`;
    }

    static get styles() {
        return [
            fontAwesome,
            css`
                .isClickable {
                    cursor: pointer;
                }
                :host {
                    --jhd-tooltip-transition: all 0.1s cubic-bezier(0.84, -0.18, 0.65, 1.13);
                    --jhd-tooltip-background-color: var(--omega-secondary);
                }
                :host([light]) {
                    --jhd-tooltip-background-color: var(--primary-background);
                }
                :host([icon]) {
                    display: inline-block;
                }

                .jhd-tooltip-icon {
                    font-size: 16px;
                    color: var(--omega-secondary-lighten-100);
                }

                span {
                    display: var(--tooltip-span-display, flex);
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    white-space: nowrap;
                    min-width: 25px;
                }

                #tooltip-trigger {
                    background: none;
                    border: none;
                    margin: 0;
                    padding: 0;
                }

                .jhd-tooltip.tooltip-light {
                    --jhd-tooltip-background-color: var(--primary-background);
                    color: var(--omega-text-default);
                    border-radius: 7px;
                    filter: drop-shadow(0 0 5px var(--omega-secondary-lighten-200));
                }

                .jhd-tooltip {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    text-align: left;
                    white-space: normal;
                }

                .jhd-tooltip:focus-within {
                    outline: none;
                }

                .jhd-tooltip::before,
                .jhd-tooltip {
                    opacity: 0;
                    position: absolute;
                    z-index: -100;
                }

                .jhd-tooltip::before {
                    border-style: solid;
                    border-width: 16px 12px 0 12px;
                    border-color: var(--jhd-tooltip-background-color) transparent transparent
                        transparent;
                    content: '';
                    transition: var(--tooltip-transition);
                    bottom: -8px;
                }

                .jhd-tooltip.tooltip-bottom-right::before,
                .jhd-tooltip.tooltip-bottom-left::before,
                .jhd-tooltip.tooltip-bottom::before {
                    border-width: 0 12px 16px 12px;
                    border-color: transparent transparent var(--jhd-tooltip-background-color)
                        transparent;
                    top: -8px;
                    bottom: auto;
                }

                .jhd-tooltip.tooltip-top-right::before,
                .jhd-tooltip.tooltip-bottom-right::before {
                    left: 3px;
                }
                .jhd-tooltip.tooltip-top-left::before,
                .jhd-tooltip.tooltip-bottom-left::before {
                    right: 3px;
                }

                .jhd-tooltip.tooltip-right-bottom::before,
                .jhd-tooltip.tooltip-right-top::before,
                .jhd-tooltip.tooltip-right::before {
                    border-width: 12px 16px 12px 0;
                    border-color: transparent var(--jhd-tooltip-background-color) transparent
                        transparent;
                    left: -8px;
                    bottom: auto;
                }

                .jhd-tooltip.tooltip-left-bottom::before,
                .jhd-tooltip.tooltip-left-top::before,
                .jhd-tooltip.tooltip-left::before {
                    border-width: 12px 0 12px 16px;
                    border-color: transparent transparent transparent
                        var(--jhd-tooltip-background-color);
                    right: -8px;
                    bottom: auto;
                }

                .jhd-tooltip.tooltip-right-top::before,
                .jhd-tooltip.tooltip-left-top::before {
                    top: var(--jhd-tooltip-indicator-top, 45px);
                }

                .jhd-tooltip.tooltip-right-bottom::before,
                .jhd-tooltip.tooltip-left-bottom::before {
                    bottom: 45px;
                    top: auto;
                }

                .jhd-tooltip {
                    background: var(--jhd-tooltip-background-color);
                    border-radius: 0.25em;
                    bottom: calc(100% + 12px);
                    color: var(--omega-info-lighten-300);
                    font-size: 12px;
                    padding: 16px;
                    transition: var(--tooltip-transition);
                    width: max-content;
                    visibility: hidden;
                }

                .jhd-tooltip.tooltip-bottom {
                    top: calc(100% + 12px);
                    bottom: auto;
                }

                .jhd-tooltip.tooltip-right-top,
                .jhd-tooltip.tooltip-right-bottom,
                .jhd-tooltip.tooltip-right {
                    left: calc(100% + 12px);
                    bottom: auto;
                }

                .jhd-tooltip.tooltip-left-bottom,
                .jhd-tooltip.tooltip-left-top,
                .jhd-tooltip.tooltip-left {
                    right: calc(100% + 12px);
                    bottom: auto;
                }

                .jhd-tooltip.tooltip-right-top,
                .jhd-tooltip.tooltip-left-top {
                    top: var(--jhd-tooltip-left-top-top, -50px);
                }

                .jhd-tooltip.tooltip-right-bottom,
                .jhd-tooltip.tooltip-left-bottom {
                    bottom: -50px;
                    top: auto;
                }

                .jhd-tooltip.tooltip-bottom-right {
                    left: 0;
                    top: calc(100% + 12px);
                    bottom: auto;
                }

                .jhd-tooltip.tooltip-top-right {
                    left: 0;
                }

                .jhd-tooltip.tooltip-top-left {
                    right: 0;
                }

                .jhd-tooltip.tooltip-bottom-left {
                    right: -4px;
                    top: calc(100% + 12px);
                    bottom: auto;
                }

                .jhd-tooltip-close-button {
                    position: absolute;
                    right: 10px;
                    top: 7px;
                    background: none;
                    border: none;
                    padding: 0;
                }
                .jhd-tooltip-close {
                    font-weight: normal;
                    color: var(--omega-text-tertiary);
                    cursor: pointer;
                    font-size: var(--omega-label);
                }

                :host([show]) .jhd-tooltip::before,
                :host([show]) .jhd-tooltip,
                :host(:not([isClickable]):hover) .jhd-tooltip::before,
                :host(:not([isClickable]):focus-within) .jhd-tooltip::before,
                :host(:not([isClickable]):hover) .jhd-tooltip,
                :host(:not([isClickable]):focus-within) .jhd-tooltip {
                    opacity: 1;
                    z-index: 100;
                    visibility: visible;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: JhdTooltip;
    }
}
